import { useNavigate, useParams } from 'react-router-dom'
import { useBackofficeTenant, useSetTenantActive } from '@/hooks/backoffice/useBackofficeTenants'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  OPERATOR: 'Operador',
  FINANCIAL: 'Financeiro',
}

export function TenantDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useBackofficeTenant(id ?? '')
  const setActive = useSetTenantActive()

  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>
  }

  const handleToggle = async () => {
    if (
      !confirm(
        data.active
          ? `Desativar "${data.name}"? Os usuários não conseguirão mais logar.`
          : `Reativar "${data.name}"?`,
      )
    )
      return
    await setActive.mutateAsync({ id: data.id, active: !data.active })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">{data.name}</h1>
            <StatusBadge active={data.active} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">CNPJ {data.cnpj}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/backoffice/tenants/${data.id}/edit`)}>
            Editar
          </Button>
          <Button variant={data.active ? 'destructive' : 'default'} onClick={handleToggle}>
            {data.active ? 'Desativar' : 'Reativar'}
          </Button>
        </div>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          Usuários ({data.users.length})
        </h2>
        {data.users.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum usuário cadastrado neste tenant.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Nome</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Role</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/backoffice/users/${u.id}`)}
                    className="cursor-pointer border-t border-border hover:bg-muted/30"
                  >
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                    <td className="px-3 py-2">{ROLE_LABEL[u.role] ?? u.role}</td>
                    <td className="px-3 py-2">
                      <StatusBadge active={u.active} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
